// @ts-strict-ignore
import React, { useMemo, useState } from 'react'
import { View } from 'react-native'

import moment from 'moment'
import styled, { css } from 'styled-components/native'

import Button from 'APP/Converse/Button'
import Typography from 'APP/Converse/Typography'
import IconCard from 'APP/Converse/IconCard'
import useShowBookingSheet from 'APP/Hooks/useShowBookingSheet'
import { DATE_FORMAT, DEFAULT_AVAILABILITY } from 'APP/Redux/AppointmentBookingRedux'
import I18n from 'APP/I18n'
import { Colors, Metrics } from 'APP/Themes'

import { AvailabilitySlot } from '@dialogue/timekeeper'
import BottomSheetModal from 'APP/Components/BottomSheetModal'
import i18n from 'APP/I18n'
import { LoadingIndicator } from '../common/loadingIndicator'
import { NoAvailableSlots } from './noAvailableSlots'
import { TimeFrameMessage } from '../common/timeFrameMessage'
import type { CustomHeaderText } from '../AppointmentBookingBase/types'
import { Availabilities, AvailabilitiesExists } from '../utils/types'
import { DayPicker } from './dayPicker'
import { TimeslotSection } from './timeslotSection'
import { createDays, getTimeframeUnit } from '../utils/helpers'
import { isOCAppointment } from 'APP/Lib/AppointmentManagementHelpers'
import { useTheme } from 'styled-components/native'

const Root = styled.View`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${Colors.selectedBg};
`

const ContentGrid = styled.View`
  width: 100%;
  margin: 0;
`

const headerMessageStyle = css`
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
  margin-right: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin * 0.875}px;
  margin-left: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const StyledTimeframe = styled(TimeFrameMessage)`
  ${headerMessageStyle}
`

const StyledHeaderText = styled(Typography)`
  ${headerMessageStyle}
  text-align: center;
`

const QuitButtonWrapper = styled(View)`
  min-height: ${({ theme }) => theme.metrics.button.height - 2}px;
`

const TopContainer = styled.View`
  background-color: ${Colors.elementsBg};
`

const TimeslotSectionStyled = styled(TimeslotSection)`
  margin-bottom: 24px;
`

const TimeSlotForDay = styled.View`
  padding: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
  padding-bottom: ${({ theme }) => Math.max(theme.insets.bottom, theme.metrics.baseMargin * 1.5)}px;
`

const SummaryTypographyStyled = styled(Typography)`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin * 0.875}px;
`

const BottomSheetStyled = styled(BottomSheetModal)`
  display: flex;
  align-items: center;
`

const BottomSheetContainer = styled.View`
  padding-left: ${Metrics.baseMargin};
  padding-right: ${Metrics.baseMargin};
  padding-bottom: ${({ theme }) => theme.insets.bottom || theme.metrics.baseMargin}px;
  display: flex;
  align-items: center;
  word-wrap: break-word;
  max-width: 550px;
  flex-wrap: wrap;
`

export interface TimeslotSelectionProps {
  appointmentType: string
  availabilities: Readonly<Availabilities>
  availabilitiesExists: Readonly<AvailabilitiesExists>
  dateFrom: moment.Moment
  dateUntil: moment.Moment
  hasExistingAppointment: boolean
  selectDay: (day: moment.Moment) => void
  selectedDay: moment.Moment
  selectedAppointment?: AvailabilitySlot | null
  selectAppointment: (availableAppointment: AvailabilitySlot) => void
  quitScheduling: () => void
  customHeaderText?: CustomHeaderText
  /**
   * for testing purposes
   */
  now?: moment.Moment
}

export enum SheetType {
  CANCEL_APPOINTMENT = 'cancelAppointmentSheet',
  AVAILABLE_APPOINTMENT = 'availableAppointmentSheet',
}

interface BookingBottomSheetProps {
  isVisible: boolean
  onCloseModal: () => void
  onConfirmCancel: () => void
  sheetType: SheetType
}

interface TextProps {
  title: string
  subtitle: string
  descriptionTitle: string
  description: string
  button: string
  analyticsName: string
}

interface TextMappingProps {
  [SheetType.CANCEL_APPOINTMENT]: TextProps
  [SheetType.AVAILABLE_APPOINTMENT]: TextProps
}

export const TimeslotSelection: React.FC<TimeslotSelectionProps> = ({
  appointmentType,
  availabilities,
  availabilitiesExists,
  dateFrom,
  dateUntil,
  hasExistingAppointment,
  selectDay,
  selectedDay,
  quitScheduling,
  selectedAppointment,
  selectAppointment,
  customHeaderText,
  now,
}) => {
  now = now || dateFrom
  const showAvailableAppointmentSheet = useShowBookingSheet(appointmentType)
  const [isSheetVisible, setIsSheetVisible] = useState(false)

  // `availabilities` only holds the days the member has opened; the exists map behind the
  // day-tab dots covers the whole window. The selected day is skipped: it is the empty one.
  const firstAvailableDay = useMemo(
    () =>
      createDays(now, dateUntil).find(
        (day) => !day.isSame(selectedDay, 'day') && availabilitiesExists[day.format(DATE_FORMAT)]
      ),
    [now, dateUntil, selectedDay, availabilitiesExists]
  )
  const hasSomeAvailabilities = !!firstAvailableDay

  const formattedSelectedDay = selectedDay.format(DATE_FORMAT)
  const availabilitiesForSelectedDay = availabilities[formattedSelectedDay] ?? DEFAULT_AVAILABILITY
  const { isLoading, hasAvailabilities, times } = availabilitiesForSelectedDay

  const timeframeUnit = getTimeframeUnit(dateUntil, dateFrom)
  const difference = dateUntil.diff(dateFrom, timeframeUnit)

  const handleOpenSheet = () => setIsSheetVisible(true)
  const handleCloseSheet = () => setIsSheetVisible(false)
  const handleSubmitSheet = () => {
    handleCloseSheet()
    quitScheduling()
  }
  const showCancelAppointmentSheet = hasExistingAppointment

  const renderAppointmentsList = () => {
    return (
      <View>
        {Object.entries(times || {}).map(([partOfDay, availabilities]) => {
          return availabilities.length > 0 ? (
            <TimeslotSectionStyled
              key={partOfDay}
              title={I18n.t(`AppointmentBooking.${partOfDay.toLowerCase()}`)}
              appointments={availabilities}
              selectedAppointment={selectedAppointment}
              onSelect={selectAppointment}
            />
          ) : null
        })}
        <QuitButtonWrapper>
          {!selectedAppointment && !isOCAppointment(appointmentType) && (
            <>
              <SummaryTypographyStyled>
                {I18n.t('AppointmentBooking.showingAllAvailable', {
                  context: timeframeUnit,
                  difference,
                })}
              </SummaryTypographyStyled>
              <Button
                small={false}
                variant="secondary"
                widthVariant="full"
                testID="APPOINTMENT_BOOKING_NONE_OF_THESE_WORK_BTN"
                title={I18n.t('AppointmentBooking.get_help_scheduling_later') || undefined}
                onPress={
                  showCancelAppointmentSheet || showAvailableAppointmentSheet
                    ? handleOpenSheet
                    : quitScheduling
                }
                analyticsName="appointmentBooking.getHelpSchedulingLater"
              />
            </>
          )}
        </QuitButtonWrapper>
      </View>
    )
  }

  return (
    <>
      <Root testID="TIMESLOT_SELECTION">
        <ContentGrid>
          <TopContainer>
            {customHeaderText ? (
              <StyledHeaderText variant="bodyNormal">
                {customHeaderText({ dateFrom, dateUntil })}
              </StyledHeaderText>
            ) : (
              <StyledTimeframe
                dateFrom={dateFrom}
                dateUntil={dateUntil}
                messageKey="AppointmentBooking.recommended_timeframe"
              />
            )}
            <DayPicker
              dateFrom={now}
              dateUntil={dateUntil}
              selectedDate={selectedDay}
              onSelect={selectDay}
              availabilitiesExists={availabilitiesExists}
            />
          </TopContainer>
          {!isLoading && (
            <TimeSlotForDay>
              {hasAvailabilities ? (
                renderAppointmentsList()
              ) : (
                <NoAvailableSlots
                  onQuit={handleSubmitSheet}
                  dayDisplayedFor={selectedDay}
                  dateRange={timeframeUnit}
                  difference={difference}
                  hasNextAvailability={hasSomeAvailabilities}
                  nextAvailability={firstAvailableDay}
                  onNavigateToFirstAvailable={() => {
                    if (firstAvailableDay) {
                      selectDay(firstAvailableDay)
                    }
                  }}
                />
              )}
            </TimeSlotForDay>
          )}
        </ContentGrid>
        {isLoading && <LoadingIndicator />}
      </Root>
      <BookingBottomSheet
        isVisible={isSheetVisible}
        onCloseModal={handleCloseSheet}
        onConfirmCancel={handleSubmitSheet}
        sheetType={
          showCancelAppointmentSheet
            ? SheetType.CANCEL_APPOINTMENT
            : SheetType.AVAILABLE_APPOINTMENT
        }
      />
    </>
  )
}

export const BookingBottomSheet: React.FC<BookingBottomSheetProps> = ({
  isVisible,
  onCloseModal,
  onConfirmCancel,
  sheetType,
}) => {
  const theme = useTheme()

  const textMapping: TextMappingProps = {
    [SheetType.CANCEL_APPOINTMENT]: {
      title: i18n.t('AppointmentBooking.cancelAppointmentSheet.title'),
      subtitle: i18n.t('AppointmentBooking.cancelAppointmentSheet.subtitle'),
      descriptionTitle: i18n.t('AppointmentBooking.cancelAppointmentSheet.descriptionTitle'),
      description: i18n.t('AppointmentBooking.cancelAppointmentSheet.description'),
      button: i18n.t('AppointmentBooking.cancelAppointmentSheet.continue'),
      analyticsName: 'Yes, cancel appointment from reschedule',
    },
    [SheetType.AVAILABLE_APPOINTMENT]: {
      title: i18n.t('AppointmentBooking.availableAppointmentSheet.title'),
      subtitle: i18n.t('AppointmentBooking.availableAppointmentSheet.subtitle'),
      descriptionTitle: i18n.t('AppointmentBooking.availableAppointmentSheet.descriptionTitle'),
      description: i18n.t('AppointmentBooking.availableAppointmentSheet.description'),
      button: i18n.t('AppointmentBooking.availableAppointmentSheet.continue'),
      analyticsName: 'Yes, manually assist with scheduling',
    },
  }
  return (
    <BottomSheetStyled
      isVisible={isVisible}
      title={textMapping[sheetType].title}
      subtitle={textMapping[sheetType].subtitle}
      onClose={onCloseModal}
      testID="NONE_OF_THESE_WORK_MODAL"
      goBack={null}
      style={null}
    >
      <BottomSheetContainer>
        <IconCard
          title={textMapping[sheetType].descriptionTitle}
          description={textMapping[sheetType].description}
          leftIcon={{
            src: 'info-outline',
            color: 'accent',
          }}
          style={{
            backgroundColor: theme.colors.selected,
          }}
          testID="TIMESLOT_SELECTION_NOTICE"
        />
        <Button
          small={false}
          onPress={onConfirmCancel}
          variant="tertiaryAlert"
          title={textMapping[sheetType].button}
          analyticsName={textMapping[sheetType].analyticsName}
          widthVariant="full"
          testID="NONE_OF_THESE_WORK_CONTINUE"
          textColor="error"
        />
      </BottomSheetContainer>
    </BottomSheetStyled>
  )
}
